<template>
  <div class="smart-redraw-notes">
    <template v-if="isLoading">
      <div class="smart-redraw-notes__loading">
        <UseSvg class="smart-redraw-notes__loading-icon" icon="stars" width="56" height="56" />
        <span class="smart-redraw-notes__loading-text">Processing your<br />instructions…</span>
      </div>
    </template>
    <template v-else>
      <div class="smart-redraw-notes__header">
        <h3 class="smart-redraw-notes__title">Instructions for our designers</h3>
      </div>
      <div class="smart-redraw-notes__textarea-wrapper">
        <textarea
          v-if="isEditableState"
          v-model="instructions"
          class="smart-redraw-notes__textarea"
          rows="3"
          :maxlength="REDRAW_NOTES_MAX_LENGTH"
          placeholder='Example: "Change the color of the font to black. Remove the background from the top half of the image."'
          :disabled="isLoading"
        />
        <div v-else class="smart-redraw-notes__final-text">
          {{ instructions }}
        </div>
        <div v-if="showEditButton" class="smart-redraw-notes__textarea-actions">
          <UseSvg
            class="smart-redraw-notes__edit-icon"
            icon="edit"
            width="18"
            height="18"
            @click="handleEdit"
          />
          <UseSvg
            v-if="isFinalState"
            class="smart-redraw-notes__delete-icon"
            icon="trash"
            width="18"
            height="18"
            @click="handleDelete"
          />
        </div>
      </div>

      <div v-if="showWarning" class="smart-redraw-notes__warning">
        <UseSvg
          class="smart-redraw-notes__warning-icon"
          icon="alert-circle"
          width="24"
          height="24"
        />
        <span class="smart-redraw-notes__warning-text">
          Please review and finalize the instructions before adding to cart.</span
        >
      </div>

      <div
        v-for="(question, questionIndex) in questions"
        v-show="isQuestionsState && !isLoading"
        :key="questionIndex"
        class="smart-redraw-notes__question-section"
      >
        <p class="smart-redraw-notes__question">{{ question.title }}</p>
        <div class="smart-redraw-notes__options">
          <label
            v-for="(option, optionIndex) in question.options"
            :key="optionIndex"
            class="smart-redraw-notes__option"
          >
            <input
              v-model="question.answer"
              type="radio"
              :value="option"
              :disabled="isFinalState || isLoading"
              class="smart-redraw-notes__radio"
            />
            <span class="smart-redraw-notes__radio-button"></span>
            <span class="smart-redraw-notes__option-text">{{ option }}</span>
          </label>
        </div>
      </div>

      <div v-if="showActions && !isLoading" class="smart-redraw-notes__actions">
        <button class="smart-redraw-notes__cancel" @click.prevent="handleCancel">Cancel</button>
        <button
          class="smart-redraw-notes__submit"
          :disabled="isSubmitDisabled"
          @click.prevent="handleSubmit"
        >
          Submit
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import UseSvg from "components/jiffy/vue-cart/UseSvg.vue";
import { api } from "jiffy-api";
import { mapActions, mapState } from "pinia";
import { REDRAW_NOTES_MAX_LENGTH } from "common/redraw-constants";
import { objNamesToCamelCase } from "common/utils/helpers/cases";
import {
  submitDtfAiRedrawNoteSubmitted,
  submitDtfAiRedrawQuestionsAnswered,
  submitDtfAiRedrawInterpretationComplete,
  submitDtfAiRedrawCancelled,
  submitDtfAiRedrawInterpretationFailed
} from "common/utils/mixpanel/submit-dtf-events";
import { useDtfStore } from "./store";

const STATES = {
  INITIAL: "initial",
  TYPING: "typing",
  QUESTIONS: "questions",
  FINAL: "final"
};

export default {
  name: "SmartRedrawNotes",
  components: {
    UseSvg
  },
  props: {
    artwork: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      default: 0
    }
  },
  setup() {
    return { REDRAW_NOTES_MAX_LENGTH };
  },
  data() {
    return {
      instructions: "",
      questions: [],
      currentState: STATES.INITIAL,
      isLoading: false,
      previousState: null,
      previousInstructions: "",
      previousQuestions: [],
      llmRedrawRequestId: null
    };
  },
  computed: {
    ...mapState(useDtfStore, ["isStandardArtwork"]),
    isEditableState() {
      return this.currentState === STATES.INITIAL || this.currentState === STATES.TYPING;
    },
    showEditButton() {
      return this.currentState === STATES.QUESTIONS || this.currentState === STATES.FINAL;
    },
    isFinalState() {
      return this.currentState === STATES.FINAL;
    },
    isQuestionsState() {
      return this.currentState === STATES.QUESTIONS;
    },
    showActions() {
      return this.currentState === STATES.TYPING || this.currentState === STATES.QUESTIONS;
    },
    isSubmitDisabled() {
      if (this.isStandardArtwork && !this.artwork.enhancementCompleted) return true;

      if (this.currentState === STATES.TYPING) {
        return !this.instructions.trim();
      }
      if (this.currentState === STATES.QUESTIONS) {
        return !this.areAllQuestionsAnswered;
      }
      return true;
    },
    areAllQuestionsAnswered() {
      return (
        this.questions.length > 0 &&
        this.questions.every(question => question.answer !== null && question.answer !== undefined)
      );
    },
    finalizedRedrawRequestId() {
      return this.isFinalState ? this.llmRedrawRequestId : null;
    },
    currentStateData() {
      return {
        currentState: this.currentState,
        instructions: this.instructions,
        questions: this.questions,
        llmRedrawRequestId: this.llmRedrawRequestId,
        previousState: this.previousState,
        previousInstructions: this.previousInstructions,
        previousQuestions: this.previousQuestions
      };
    },
    showWarning() {
      return (
        this.artwork.enhancementTier === "custom" &&
        !this.isFinalState &&
        this.currentState !== STATES.INITIAL
      );
    },
    artworkId() {
      if (typeof this.artwork.id === "number") {
        return this.artwork.id;
      }
      return this.artwork.id.split("-")[0];
    }
  },
  watch: {
    instructions(newVal) {
      if (this.previousState === null && !this.isLoading) {
        if (this.currentState === STATES.INITIAL && newVal.trim()) {
          this.currentState = STATES.TYPING;
        } else if (
          this.currentState === STATES.TYPING &&
          !newVal.trim() &&
          this.questions.length === 0
        ) {
          this.currentState = STATES.INITIAL;
        }
      }
      if (!this.isLoading) {
        this.saveState();
      }
    },
    questions: {
      deep: true,
      handler() {
        if (!this.isLoading && this.currentState === STATES.QUESTIONS) {
          this.saveState();
        }
      }
    }
  },
  mounted() {
    this.loadSavedState();
  },
  methods: {
    ...mapActions(useDtfStore, ["updateArtworkValues"]),
    loadSavedState() {
      const { llmInterpretationState, enhancementNote, llmRedrawRequestId } = this.artwork;

      if (llmInterpretationState) {
        Object.assign(this, {
          currentState: llmInterpretationState.currentState || STATES.INITIAL,
          instructions: llmInterpretationState.instructions || "",
          questions: llmInterpretationState.questions || [],
          previousState: llmInterpretationState.previousState ?? null,
          previousInstructions: llmInterpretationState.previousInstructions || "",
          previousQuestions: llmInterpretationState.previousQuestions || []
        });
      } else if (enhancementNote) {
        this.instructions = enhancementNote;
        this.currentState = STATES.FINAL;
      }

      if (llmRedrawRequestId) {
        this.llmRedrawRequestId = llmRedrawRequestId;
      }
    },
    saveState() {
      const dataToSave = {
        index: this.index,
        llmInterpretationState: this.currentStateData
      };

      if (this.instructions && this.currentState === STATES.FINAL) {
        dataToSave.enhancementNote = this.instructions;
      } else {
        dataToSave.enhancementNote = null;
      }

      if (this.llmRedrawRequestId) {
        dataToSave.llmRedrawRequestId = this.llmRedrawRequestId;
      }

      this.updateArtworkValues(dataToSave);
    },
    saveCurrentStateForEdit() {
      this.previousState = this.currentState;
      this.previousInstructions = this.instructions;
      this.previousQuestions = this.questions.map(q => ({ ...q }));
    },
    restorePreviousState() {
      if (this.previousState !== null) {
        this.currentState = this.previousState;
        this.instructions = this.previousInstructions;
        this.questions = this.previousQuestions.map(q => ({ ...q }));
        this.clearPreviousState();
        this.saveState();
      }
    },
    clearPreviousState() {
      this.previousState = null;
      this.previousInstructions = "";
      this.previousQuestions = [];
    },
    transformApiQuestions(clarifyingQuestions) {
      return clarifyingQuestions.map(q => ({
        title: q.question || q.text || q.title || "Question",
        options: q.options || [],
        answer: null
      }));
    },
    handleApiError() {
      this.questions = [];
      this.llmRedrawRequestId = null;
      this.finalize();
    },
    handleCancel() {
      // Track the cancellation with current step
      submitDtfAiRedrawCancelled(this.artwork.id, this.currentState);

      if (this.previousState !== null) {
        this.restorePreviousState();
      } else if (this.currentState === STATES.TYPING && this.questions.length === 0) {
        this.instructions = "";
        this.currentState = STATES.INITIAL;
        this.saveState();
      } else if (this.currentState === STATES.QUESTIONS) {
        this.llmRedrawRequestId = null;
        this.questions = [];
        this.currentState = STATES.TYPING;
        this.saveState();
      }
    },
    finalize() {
      this.currentState = STATES.FINAL;
      this.saveState();
    },
    async handleSubmit() {
      this.clearPreviousState();
      this.isLoading = true;

      try {
        if (this.currentState === STATES.TYPING) {
          await this.submitInitialInstructions();
        } else if (this.currentState === STATES.QUESTIONS) {
          await this.submitQuestionAnswers();
        }
      } finally {
        this.isLoading = false;
      }
    },
    async submitInitialInstructions() {
      try {
        const response = await api.llmRedrawRequests.create({
          llm_redraw_request: {
            dtf_artwork_id: this.artworkId,
            initial_prompt: this.instructions
          }
        });

        // Track the note submission
        submitDtfAiRedrawNoteSubmitted(this.artwork.id, this.instructions);

        // Convert snake_case response to camelCase
        const camelResponse = objNamesToCamelCase(response);

        this.llmRedrawRequestId = camelResponse.id;
        const clarifyingQuestions = camelResponse.clarifyingQuestions || [];

        if (clarifyingQuestions.length > 0) {
          this.questions = this.transformApiQuestions(clarifyingQuestions);
          this.currentState = STATES.QUESTIONS;
          this.saveState();
        } else {
          // Track interpretation complete for initial submission (no follow-up questions)
          submitDtfAiRedrawInterpretationComplete(this.artwork.id, "initial_submission");
          this.finalize();
        }
      } catch (error) {
        // Track interpretation failed
        submitDtfAiRedrawInterpretationFailed(this.artwork.id, "initial_submission");
        this.handleApiError();
      }
    },
    async submitQuestionAnswers() {
      try {
        const followUpQuestionsAndResponses = this.questions.reduce((acc, question, index) => {
          const key = question.title || `question_${index}`;
          acc[key] = question.answer;
          return acc;
        }, {});

        await api.llmRedrawRequests.update(
          { id: this.llmRedrawRequestId },
          {
            llm_redraw_request: {
              follow_up_questions_and_responses: followUpQuestionsAndResponses
            }
          }
        );

        // Track the questions answered
        submitDtfAiRedrawQuestionsAnswered(this.artwork.id, followUpQuestionsAndResponses);

        // Track interpretation complete for final submission
        submitDtfAiRedrawInterpretationComplete(this.artwork.id, "final_submission");
        this.finalize();
      } catch (error) {
        // Track interpretation failed
        submitDtfAiRedrawInterpretationFailed(this.artwork.id, "questions");
        this.handleApiError();
      }
    },
    handleEdit() {
      this.saveCurrentStateForEdit();
      this.currentState = STATES.TYPING;
      this.saveState();
    },
    handleDelete() {
      // Track the deletion/cancellation
      submitDtfAiRedrawCancelled(this.artwork.id, this.currentState);

      this.instructions = "";
      this.questions = [];
      this.currentState = STATES.INITIAL;
      this.isLoading = false;
      this.llmRedrawRequestId = null;
      this.clearPreviousState();
      this.saveState();
    }
  }
};
</script>

<style scoped lang="scss">
@import "frontend/init/jiffy/settings";
@import "frontend/init/jiffy/mixin";

.smart-redraw-notes {
  $this: &;

  background: linear-gradient(135deg, #d7e4f6 0%, #ecf4ff 100%);
  border: 1px solid rgba(38, 38, 152, 0.3);
  border-radius: 8px;
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title {
    font-family: Arial, sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 16px;
    color: $mirage;
    margin: 0;
  }

  &__link {
    font-family: Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 16px;
    color: $primary-light;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__textarea-wrapper {
    position: relative;
  }

  &__textarea {
    width: 100%;
    background: $white;
    border: 1px solid $primary-light;
    border-radius: 4px;
    padding: 8px;
    font-family: Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: $mirage;
    resize: none;
    box-shadow:
      0px 4px 6px -2px rgba(18, 25, 38, 0.03),
      0px 12px 16px -4px rgba(18, 25, 38, 0.08);
    outline: none;
    vertical-align: top;
    transition: border-color 0.2s ease;

    &::placeholder {
      color: $enhance-grey-modern-400;
    }

    &:focus {
      border-color: $primary-light;
    }

    &:disabled {
      background: #f5f5f5;
      cursor: not-allowed;
    }
  }

  &__textarea-actions {
    position: absolute;
    bottom: 8px;
    right: 8px;
    display: flex;
    gap: 8px;
  }

  &__edit-icon {
    cursor: pointer;
    color: $enhance-grey-modern-400;

    &:hover {
      color: $primary-light;
    }
  }

  &__delete-icon {
    cursor: pointer;
    color: $enhance-grey-modern-400;

    &:hover {
      color: #e74c3c;
    }
  }

  &__warning {
    display: flex;
    align-items: center;
    gap: 8px;
    background: #f8fafc;
    border: 1px solid #e1e1e5;
    border-radius: 8px;
    padding: 8px 12px;
  }

  &__warning-icon {
    flex-shrink: 0;
    color: $tahiti-gold;
  }

  &__warning-text {
    font-family: Arial, sans-serif;
    font-weight: 400;
    font-size: 13px;
    line-height: 18px;
    color: #364152;
  }

  &__loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 12px;
  }

  &__loading-icon {
    color: $primary-light;
  }

  &__loading-text {
    font-family: Arial, sans-serif;
    font-weight: 400;
    font-size: 16px;
    line-height: 20px;
    color: #4b5565;
    text-align: center;
  }

  &__question-section {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__question {
    font-family: Arial, sans-serif;
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
    color: #121926;
    margin: 0;
  }

  &__options {
    display: flex;
    flex-direction: column;
    gap: 0;
  }

  &__option {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 0;
    cursor: pointer;

    &:has(.smart-redraw-notes__radio:disabled) {
      cursor: default;
      opacity: 0.7;
    }
  }

  &__radio {
    display: none;

    &:checked + #{$this}__radio-button {
      border-color: $primary-light;

      &::after {
        content: "";
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        width: 10px;
        height: 10px;
        background-color: $primary-light;
        border-radius: 50%;
      }
    }
  }

  &__radio-button {
    width: 20px;
    height: 20px;
    border: 2px solid #9aa3b2;
    border-radius: 50%;
    position: relative;
    flex-shrink: 0;
    transition: border-color 0.2s ease;
  }

  &__option-text {
    font-family: Arial, sans-serif;
    font-weight: 400;
    font-size: 15px;
    line-height: 24px;
    color: #121926;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
  }

  &__cancel {
    font-family: Inter, sans-serif;
    font-weight: 600;
    font-size: 15px;
    line-height: 24px;
    color: $gray-modern-600;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    min-width: 60px;
    transition: opacity 0.2s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  &__submit {
    font-family: Inter, sans-serif;
    font-weight: 600;
    font-size: 15px;
    line-height: 24px;
    color: $white;
    background: $primary-light;
    border: none;
    border-radius: 8px;
    padding: 0 12px;
    width: 168px;
    height: 40px;
    cursor: pointer;
    transition: background-color 0.2s ease;

    &:hover {
      background-color: darken($primary-light, 10%);
    }

    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;

      &:hover {
        background-color: $primary-light;
      }
    }
  }

  &__final-text {
    width: 100%;
    background: $white;
    border: 1px solid $primary-light;
    border-radius: 4px;
    padding: 8px;
    font-family: Arial, sans-serif;
    font-weight: 400;
    font-size: 14px;
    line-height: 18px;
    color: $mirage;
    min-height: 100px;
    box-shadow:
      0px 4px 6px -2px rgba(18, 25, 38, 0.03),
      0px 12px 16px -4px rgba(18, 25, 38, 0.08);
    white-space: pre-wrap;
    word-break: break-word;
  }
}
</style>
